import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { IconReauthModalSessionExpired } from './Icons';
import { Button } from './Button';
import { TextField } from './TextField';
import {LoginResult} from "../api/AuthApi";
import {AuthErrorCode, getErrorMessage} from "../api/types";


interface ReauthModalProps {
    open: boolean;
    onClose: () => void;
    onReauth: (password: string) => Promise<LoginResult>;
    userDisplayName?: string;
}

const ReauthModal: React.FC<ReauthModalProps> = ({
                                                     open,
                                                     onClose,
                                                     onReauth,
                                                     userDisplayName = 'your account'
                                                 }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);

    useEffect(() => {
        if (open) {
            setPassword('');
            setError(null);
            setErrorCode(null);
        }
    }, [open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!password.trim()) {
            setError('Bitte geben Sie Ihr Passwort ein.');
            return;
        }

        setLoading(true);
        setError(null);
        setErrorCode(null);

        try {
            const result = await onReauth(password);

            if (result.success) {
                setPassword('');
                setError(null);
                setErrorCode(null);

            } else {
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (err) {
            console.error('Reauth error:', err);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            disableEscapeKeyDown={true}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: 200
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    pb: 1
                }}
            >
                <IconReauthModalSessionExpired />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="span">
                        Sitzung abgelaufen
                    </Typography>
                </Box>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Ihre Sitzung ist abgelaufen. Bitte geben Sie Ihr Passwort ein, um fortzufahren.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        id="password"
                        isDisabled={loading}
                        name="password"
                        type="password"
                        placeholder="Passwort"
                        value={password}
                        onChange={(e) => setPassword(e)}
                        description={`Erneut authentifizieren als ${userDisplayName}`}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleClose}
                        isDisabled={loading}
                        variant="outlined"
                        color="primary"
                    >
                        Abbrechen und ausloggen
                    </Button>

                    <Button
                        type="submit"
                        isDisabled={loading || !password.trim()}
                        loading={loading}
                        variant="contained"
                        color="primary"
                    >
                        Einloggen
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ReauthModal;